/**
 * The counter: who has paid, and who is still waiting for it.
 *
 * Quick service inverts the one thing the rest of this terminal assumes. At a
 * table the bill comes last, so settling it ends everything — the table frees,
 * the rail clears, the tab disappears from every list. At a counter the bill
 * comes first, and the guest then stands two feet away waiting for food nobody
 * has started cooking. That gap is the whole job, and before this screen the
 * till had nowhere to show it: the moment somebody paid, their order vanished.
 *
 * So this is a list of people, not of orders. It is in the order they paid,
 * because that is the order they were promised in and the order anybody
 * standing there believes they will be served in — a queue that re-sorted
 * itself as food came up would be correct and completely unusable, because the
 * person at the front would keep changing while five people watch.
 *
 * **Ready is a block of ink, not a colour.** The same decision as `food-up.tsx`
 * and for the same reason: this is read at a glance across a hot counter, and a
 * fourth hue on a row that already carries a wait is a hue nobody reads.
 *
 * An order that was never handed over stays, but not in the queue: after
 * twelve hours it is not somebody standing at the counter, it is a tap nobody
 * made. Those fold into one line under the queue, with a button that marks
 * them all collected, so yesterday's forgotten lunch neither tops today's list
 * nor disappears without anybody saying so.
 */

import { useEffect, useState } from 'react';

import { useCollect, useCollectionQueue, trimQty, type Order } from '@restaurant/api-client';
import { ActionError, Button, ErrorNotice } from '@restaurant/ui';

import { FoodUpTag } from './food-up';
import { useFoodUp } from './readiness';

export function WaitingToCollect({
  locationId,
  onOpenOrder,
}: {
  locationId: string | undefined;
  onOpenOrder: (orderId: string) => void;
}) {
  const queue = useCollectionQueue(locationId);
  const foodUp = useFoodUp((queue.data ?? []).map((order) => order.id));
  const now = useTicking();

  // Nothing to say when nobody is waiting: the open orders below are the
  // screen, and an empty "waiting" box above them is one more thing to read.
  if (queue.isPending || !queue.data || queue.data.length === 0) return null;
  if (queue.isError) return <ErrorNotice message={queue.error.message} />;

  const waiting = queue.data.filter((order) => !stale(order, now));
  const old = queue.data.filter((order) => stale(order, now));

  return (
    <section className="ord__section" aria-label="Paid, waiting to collect">
      {waiting.length > 0 && (
        <>
          <h2 className="ord__heading">
            Paid, waiting to collect
            <span className="pos__view-count">{waiting.length}</span>
          </h2>
          <ul className="ct__list">
            {waiting.map((order) => {
              const food = foodUp(order.id);
              const up = food.ready.length > 0;
              return (
                <li key={order.id}>
                  <article className={`ct__order${up ? ' ct__order--up' : ''}`}>
                    {/* The number is the largest thing on the row because it
                        is the only part of this anybody says out loud. */}
                    <button
                      type="button"
                      className="ct__number tabular"
                      onClick={() => onOpenOrder(order.id)}
                      aria-label={`Order ${order.number}, open it`}
                    >
                      {order.number}
                    </button>

                    <div className="ct__what">
                      <p className="ct__items">{describe(order)}</p>
                      <FoodUpTag food={food} />
                    </div>

                    <span className="ct__waited tabular">{waited(order, now)}</span>

                    <Collect order={order} />
                  </article>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {old.length > 0 && <ClearOld orders={old} />}
    </section>
  );
}

const STALE_MS = 12 * 60 * 60_000;

function stale(order: Order, now: number): boolean {
  return now - Date.parse(order.opened_at) > STALE_MS;
}

/** The ones left over from an earlier service, in one line. */
function ClearOld({ orders }: { orders: Order[] }) {
  const collect = useCollect();
  const [busy, setBusy] = useState(false);

  async function clearAll() {
    setBusy(true);
    try {
      // One at a time: each is its own record that the order went out.
      for (const order of orders) await collect.mutateAsync(order.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ct__old">
      <span>
        {orders.length === 1
          ? `Order ${orders[0]?.number} is from an earlier service and was never marked collected.`
          : `${orders.length} orders from an earlier service were never marked collected.`}
      </span>
      <Button onClick={() => void clearAll()} loading={busy}>
        Mark {orders.length === 1 ? 'it' : 'them'} collected
      </Button>
      <ActionError error={collect.error} action="Clearing old orders" />
    </div>
  );
}

/**
 * Deliberately not "Hand over". That is already a control on the order screen
 * and it means passing a tab to another member of staff — two buttons a shift
 * apart, reading the same, doing entirely different things. This one records
 * that the guest took their food, which is what the ledger calls it.
 */
function Collect({ order }: { order: Order }) {
  const collect = useCollect();
  return (
    <div className="ct__act">
      <Button
        variant="primary"
        onClick={() => collect.mutate(order.id)}
        loading={collect.isPending}
      >
        Collected
      </Button>
      <ActionError error={collect.error} action="Handing an order over" />
    </div>
  );
}

/** What is in the bag, in the fewest words that still identify it. */
function describe(order: Order): string {
  const live = order.lines.filter((line) => line.state !== 'voided');
  if (live.length === 0) return 'Nothing on the order';
  // The same dish rung in twice is one count, not "2 × Chips, Chips".
  const counts = new Map<string, number>();
  for (const line of live)
    counts.set(line.name, (counts.get(line.name) ?? 0) + Number(line.qty));
  return [...counts]
    .map(([name, qty]) => (qty === 1 ? name : `${trimQty(String(qty))} × ${name}`))
    .join(', ');
}

/**
 * How long they have been standing there.
 *
 * Measured from when the order was opened rather than from when it was paid.
 * At a counter those are the same moment give or take a card tap — you order
 * and pay in one go — and the opening is the arrival, which is what the queue
 * is sorted by. Measuring from two different instants in the same list would
 * put rows out of order with their own waits.
 */
function waited(order: Order, now: number): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(order.opened_at)) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  // An order that has been waiting for hours was forgotten, and "214m" reads
  // as a number rather than as a problem.
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * A clock that moves.
 *
 * The queue refetches on its own, but every wait on this screen is derived from
 * a timestamp that does not change, so nothing would re-render between an order
 * arriving and the next one. Half a minute is the coarsest tick that still
 * keeps a minute counter honest.
 */
function useTicking(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}
